import { Box, Typography, Button, CircularProgress, Skeleton, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from "@mui/material";
import { ContentCopy, DeleteOutline } from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { loadConnectAndInitialize } from "@stripe/connect-js";
import { ConnectAccountOnboarding, ConnectComponentsProvider } from "@stripe/react-connect-js";
import { useState, useEffect } from "react";
import { getEnvVar } from "../../../../utils/get-env";
import { gateway } from "../../../../gateway";

interface AccountStatus {
  accountId: string;
  accountType?: string;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  requirementsCurrentlyDue: string[];
  requirementsEventuallyDue: string[];
}

type Severity = "success" | "error" | "warning" | "info";

const BUTTON_SX = {
  fontWeight: 700,
  textTransform: "none",
  fontSize: "1rem",
  py: 1.5,
  px: 3,
  borderRadius: 2,
  background: "linear-gradient(135deg, #635BFF 0%, rgba(99, 91, 255, 0.85) 100%)",
  color: "white",
  boxShadow: "0 4px 12px rgba(99, 91, 255, 0.3)",
  "&:hover": {
    background: "linear-gradient(135deg, #5045e6 0%, rgba(80, 69, 230, 0.85) 100%)",
    boxShadow: "0 8px 20px rgba(99, 91, 255, 0.4)",
    transform: "translateY(-2px)",
  },
  transition: "all 0.3s ease",
} as const;

export const EcommerceStripeButtonSetup = () => {
  const [feedback, setFeedback] = useState<{ severity: Severity; message: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [accountStatus, setAccountStatus] = useState<AccountStatus | null>(null);
  const [copied, setCopied] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [connectingExisting, setConnectingExisting] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [stripeConnectInstance, setStripeConnectInstance] = useState<ReturnType<typeof loadConnectAndInitialize> | null>(null);

  const publishableKey = getEnvVar("REACT_APP_STRIPE_PUBLISHABLE_KEY") || "";

  useEffect(() => {
    checkAccountStatus();
  }, []);

  // Auto-refresh status when account is pending verification
  useEffect(() => {
    if (!accountStatus || (accountStatus.chargesEnabled && accountStatus.payoutsEnabled)) return;
    const interval = setInterval(() => {
      checkAccountStatus();
    }, 30000);
    return () => clearInterval(interval);
  }, [accountStatus]);

  const checkAccountStatus = async () => {
    try {
      const response = await gateway.get("/stripe/account_status");
      setAccountStatus(response.data);
      setLoading(false);
    } catch (error: any) {
      if (error.response?.status === 404 || error.response?.status === 400) {
        setAccountStatus(null);
      } else {
        setFeedback({
          severity: "error",
          message: error.response?.data?.message || "Failed to check account status",
        });
      }
      setLoading(false);
    }
  };

  const handleConnectStripe = () => {
    if (accountStatus?.accountType === "standard") {
      window.open("https://dashboard.stripe.com/account", "_blank", "noopener,noreferrer");
      return;
    }
    if (!publishableKey) {
      setFeedback({ severity: "error", message: "Stripe onboarding is not configured. Please contact support." });
      return;
    }
    if (!stripeConnectInstance) {
      const fetchClientSecret = async () => {
        try {
          const response = await gateway.post("/stripe/account_session");
          const { client_secret: clientSecret } = response.data;
          if (!clientSecret) {
            throw new Error("No client secret received from server");
          }
          return clientSecret;
        } catch (error: any) {
          const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || "An error occurred";
          console.error("Error creating Stripe account session: ", errorMessage);
          setFeedback({ severity: "error", message: errorMessage || "Failed to initialize Stripe. Please try again." });
          return undefined;
        }
      };

      setStripeConnectInstance(loadConnectAndInitialize({ publishableKey, fetchClientSecret }));
    }
    setShowOnboarding(true);
    setFeedback(null);
  };

  const handleConnectExisting = async () => {
    setConnectingExisting(true);
    setFeedback(null);
    try {
      const response = await gateway.get("/stripe/oauth/authorize");
      sessionStorage.setItem("stripeOAuthReturnPath", window.location.pathname);
      window.location.assign(response.data.url);
    } catch (error: any) {
      setFeedback({ severity: "error", message: error.response?.data?.message || "Could not connect an existing Stripe account." });
      setConnectingExisting(false);
    }
  };

  const handleOnboardingExit = async () => {
    console.log("User exited onboarding");
    setShowOnboarding(false);
    await checkAccountStatus();
  };

  const handleCopyAccountId = (accountId: string) => {
    navigator.clipboard.writeText(accountId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    setFeedback(null);
    try {
      await gateway.delete("/stripe/account");
      setFeedback({
        severity: "success",
        message: "Stripe account disconnected successfully",
      });
      setAccountStatus(null);
      setConfirmDialogOpen(false);
    } catch (error: any) {
      setFeedback({
        severity: "error",
        message: error.response?.data?.message || "Failed to disconnect Stripe account",
      });
    } finally {
      setDisconnecting(false);
    }
  };

  const isConnected = accountStatus?.chargesEnabled && accountStatus?.payoutsEnabled;
  const isPending = accountStatus?.detailsSubmitted && !isConnected;

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Button
        variant="contained"
        size="large"
        onClick={isConnected ? undefined : handleConnectStripe}
        disabled={loading || isConnected || showOnboarding}
        fullWidth
        sx={{
          ...BUTTON_SX,
          ...(isConnected
            ? {
                background: "linear-gradient(135deg, #22c55e 0%, rgba(34, 197, 94, 0.85) 100%)",
                boxShadow: "0 4px 12px rgba(34, 197, 94, 0.30)",
                "&:hover": {
                  background: "linear-gradient(135deg, #16a34a 0%, rgba(22, 163, 74, 0.85) 100%)",
                  boxShadow: "0 8px 20px rgba(34, 197, 94, 0.40)",
                  transform: "translateY(-2px)",
                },
              }
            : isPending
            ? {
                background: "linear-gradient(135deg, #f59e0b 0%, rgba(245, 158, 11, 0.85) 100%)",
                boxShadow: "0 4px 12px rgba(245, 158, 11, 0.30)",
                "&:hover": {
                  background: "linear-gradient(135deg, #d97706 0%, rgba(217, 119, 6, 0.85) 100%)",
                  boxShadow: "0 8px 20px rgba(245, 158, 11, 0.40)",
                  transform: "translateY(-2px)",
                },
              }
            : {}),
        }}
      >
        {loading ? <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} /> : null}
        {isConnected ? "Installed" : accountStatus?.accountType === "standard" ? "Review in Stripe" : isPending ? "Pending Verification" : loading ? "Loading..." : "Create Express account"}
      </Button>

      {!loading && !accountStatus && (
        <Button variant="outlined" onClick={handleConnectExisting} disabled={connectingExisting || showOnboarding} fullWidth>
          {connectingExisting ? "Opening Stripe..." : "Connect my existing Stripe account"}
        </Button>
      )}

      {/* Skeleton while loading */}
      <AnimatePresence mode="wait">
        {loading && !accountStatus ? (
          <motion.div
            key="skeleton"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1.5,
                background: "linear-gradient(135deg, rgba(100, 116, 139, 0.05) 0%, rgba(100, 116, 139, 0.02) 100%)",
                border: "1px solid rgba(100, 116, 139, 0.1)",
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Skeleton variant="text" width="75%" height={20} animation="wave" sx={{ bgcolor: "rgba(100, 116, 139, 0.15)" }} />
                <Skeleton variant="text" width="55%" height={16} animation="wave" sx={{ bgcolor: "rgba(100, 116, 139, 0.15)" }} />
              </Box>
            </Box>
          </motion.div>
        ) : null}
      </AnimatePresence>

      {/* Success message when connected */}
      <AnimatePresence mode="wait">
        {isConnected ? (
          <motion.div
            key="connected"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                p: 1.5,
                borderRadius: 1.5,
                background: "linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(34, 197, 94, 0.05) 100%)",
                border: "1px solid rgba(34, 197, 94, 0.2)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#16a34a",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                  }}
                >
                  Stripe connected
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "var(--fx-surface-textsecondary, #64748B)",
                    fontSize: "0.75rem",
                  }}
                >
                  (Account: <strong>{accountStatus.accountId}</strong>)
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Tooltip title={copied ? "Copied!" : "Copy Account ID"} arrow>
                  <IconButton
                    size="small"
                    onClick={() => handleCopyAccountId(accountStatus.accountId)}
                    sx={{
                      color: "var(--fx-surface-textsecondary, #64748B)",
                      "&:hover": { color: "#16a34a" },
                    }}
                  >
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Disconnect Stripe account" arrow>
                  <IconButton
                    size="small"
                    onClick={() => setConfirmDialogOpen(true)}
                    sx={{
                      color: "var(--fx-surface-textsecondary, #64748B)",
                      "&:hover": { color: "#ef4444" },
                    }}
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </motion.div>
        ) : null}
      </AnimatePresence>

      {/* Pending verification message */}
      <AnimatePresence mode="wait">
        {isPending ? (
          <motion.div
            key="pending"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                p: 1.5,
                borderRadius: 1.5,
                background: "linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(245, 158, 11, 0.05) 100%)",
                border: "1px solid rgba(245, 158, 11, 0.2)",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "#d97706",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                }}
              >
                Account under review
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "var(--fx-surface-textsecondary, #64748B)",
                  fontSize: "0.75rem",
                }}
              >
                Your account is being reviewed by Stripe. Auto-checking every 30s...
              </Typography>
              {accountStatus.requirementsCurrentlyDue.length > 0 && (
                <Typography
                  variant="caption"
                  sx={{
                    color: "var(--fx-surface-textsecondary, #64748B)",
                    fontSize: "0.70rem",
                    mt: 0.5,
                  }}
                >
                  Pending: {accountStatus.requirementsCurrentlyDue.join(", ")}
                </Typography>
              )}
            </Box>
          </motion.div>
        ) : null}
      </AnimatePresence>

      {/* Feedback messages */}
      <AnimatePresence mode="wait">
        {feedback ? (
          <motion.div
            key="feedback"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          >
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1.5,
                background:
                  feedback.severity === "error"
                    ? "linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(239, 68, 68, 0.05) 100%)"
                    : "linear-gradient(135deg, rgba(99, 91, 255, 0.1) 0%, rgba(99, 91, 255, 0.05) 100%)",
                border: feedback.severity === "error" ? "1px solid rgba(239, 68, 68, 0.2)" : "1px solid rgba(99, 91, 255, 0.2)",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: feedback.severity === "error" ? "#ef4444" : "#635BFF",
                  fontWeight: 500,
                  fontSize: "0.875rem",
                }}
              >
                {feedback.message}
              </Typography>
            </Box>
          </motion.div>
        ) : null}
      </AnimatePresence>

      {/* Onboarding modal */}
      {showOnboarding && !isConnected && stripeConnectInstance && (
        <Box sx={{ mt: 2 }}>
          <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
            <ConnectAccountOnboarding onExit={handleOnboardingExit} />
          </ConnectComponentsProvider>
        </Box>
      )}

      {/* Disconnect confirmation dialog */}
      <Dialog
        hideBackdrop={true}
        sx={{ zIndex: 999999999 }}
        open={confirmDialogOpen}
        onClose={() => !disconnecting && setConfirmDialogOpen(false)}
        aria-labelledby="disconnect-dialog-title"
        aria-describedby="disconnect-dialog-description"
      >
        <DialogTitle id="disconnect-dialog-title">Disconnect Stripe Account?</DialogTitle>
        <DialogContent>
          <DialogContentText id="disconnect-dialog-description">
            {accountStatus?.accountType === "standard"
              ? "Disconnect this Stripe account from FlowNect? Your Stripe account will remain active."
              : "Are you sure you want to disconnect your Stripe account? This will permanently delete the Express account created through FlowNect."}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} disabled={disconnecting}>
            Cancel
          </Button>
          <Button
            onClick={handleDisconnect}
            color="error"
            variant="contained"
            disabled={disconnecting}
            startIcon={disconnecting ? <CircularProgress size={16} /> : <DeleteOutline />}
          >
            {disconnecting ? "Disconnecting..." : "Disconnect"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
